import React, { useEffect, useState, useRef } from "react";
import Pagination from "@mui/material/Pagination";
import axiosInstance from "../../components/axiosInstance";
import { API_URLS } from "../../apiUrl";
import AddMouza from "./Addmouza";

export interface MouzaRow {
  id: number;
  mouza_name: string;
  ward_id: number;
  borough_id?: number;
  borough_primary_id?: number;
  borough_name?: string;
  municipality_id?: number;
  municipality_name?: string;
  status: number;
  created_at: string;
  updated_at: string;
  ward_no: string;
}

interface Props {
  muniCipalityName: string;
  muniCipalityId: any;
}

const MouzaListing: React.FC<Props> = ({
  muniCipalityName,
  muniCipalityId,
}) => {
  const [data, setData] = useState<MouzaRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Edit state ─────────────────────────────────────────────────────────────
  const [editingRow, setEditingRow] = useState<MouzaRow | null>(null);

  // ── Debounce ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // ── Fetch — only triggers on page / debouncedSearch change ────────────────
  const fetchData = async (pg: number, srch: string) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(API_URLS.mouzalisting, {
        page: pg,
        limit: 10,
        search: srch,
      });
      const responseData = res.data?.data;
      setData(responseData?.rows || []);
      setTotalPages(responseData?.pagination?.lastPage || 1);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page, debouncedSearch);
  }, [page, debouncedSearch]); // ✅ correct dependencies — no extra calls

  // ── Edit — fetch full data first ──────────────────────────────────────────
  const handleEdit = async (row: MouzaRow) => {
    try {
      const res = await axiosInstance.post(API_URLS.mouzalisting, {
        page: 1,
        limit: 10,
        mouza_id: row.id,
      });
      const fetched: MouzaRow = res.data?.data;
      // Map borough_primary_id → borough_id for AddMouza
      setEditingRow({
        ...fetched,
        borough_id: fetched.borough_primary_id ?? fetched.borough_id,
      });
    } catch (err) {
      console.error("Failed to fetch mouza for edit:", err);
      // Fallback: use row data from listing
      setEditingRow(row);
    }
  };

  const handleEditDone = () => {
    setEditingRow(null);
    fetchData(page, debouncedSearch); // refresh list
  };

  // ── Show AddMouza in edit mode ─────────────────────────────────────────────
  if (editingRow) {
    return (
      <AddMouza
        muniCipalityName={muniCipalityName}
        muniCipalityId={muniCipalityId}
        editData={editingRow}
        isEditing={true}
        onEditDone={handleEditDone}
      />
    );
  }

  // ── List ───────────────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto">
      <div className="flex gap-5 mb-4">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Mouza Listing
        </h1>
        <div className="flex justify-end md:w-4/12 w-full ml-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border px-3 py-2 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-900 dark:bg-gray-800"
          />
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
            <tr>
              <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Mouza Name</th>
              <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Ward</th>
              <th className="py-3 px-6 text-center text-sm font-medium text-gray-600 dark:text-gray-100">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center py-8 text-gray-400 text-sm">
                  <span className="animate-pulse">Loading...</span>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-8 text-gray-400 text-sm">
                  No Data Found
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={row.id} className="border-b dark:border-gray-700 hover:bg-gray-50">
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{row.mouza_name}</td>
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{row.ward_no}</td>
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(row)}
                        className="px-2 py-1.5 text-green-500 border border-green-200 rounded bg-green-100 hover:bg-green-200"
                        title="Edit"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_: React.ChangeEvent<unknown>, value: number) => setPage(value)}
          color="primary"
        />
      </div>
    </div>
  );
};

export default MouzaListing;